import React, {useMemo} from 'react'
import {ProductTileCms, ProductTileSkeleton} from '@/components/product-tile-cms'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    CarouselSlidePaddingType,
    CarouselSlidesVisibleTypeDesktop,
    CarouselSlidesVisibleTypeMobile,
    ContentButtonNavigation,
    navButtonTypes
} from '@/components/ui/carousel'
import {Button} from '@/components/cta'
import {useFetchCioProductCategory} from '@/hooks/constructor-io/useFetchCioProductCategory'
import {useFetchBatchedSfccProducts} from '@/hooks/product/useFetchBatchedProductData'
import {cn} from '@/lib/utils'
import {AccountFormLabelDataType} from '../account-drawer/accountDrawer.d'
import {ProductDataType} from '@/types/product'
import {useMemoizedProductData} from '../../hooks/product/useMemoizedProductData'

interface ProductCategoryCarouselProps extends AccountFormLabelDataType {
    categoryId: string
    resultCount: number
    addedToCartPodId: string
    productTileBorderColor?: string
    showPeek?: boolean
    showPeekMobile?: boolean
    slidesVisible?: CarouselSlidesVisibleTypeDesktop
    slidesVisibleMobile?: CarouselSlidesVisibleTypeMobile
    slidePadding?: CarouselSlidePaddingType
    slidePaddingMobile?: CarouselSlidePaddingType
    className?: string
    ButtonNavigationColor?: string
    enableHeadline?: boolean
    headlineText?: string
    enableNavigationArrows?: boolean
    showNavigationArrowsMobile?: boolean
    showNavigationArrowsDesktop?: boolean
    ctaText?: string
    ctaHref?: string
    ctaExternal?: boolean
    ctaExternalLink?: string
    enableBasicView?: boolean
}

export const ProductCategoryCarousel: React.FC<ProductCategoryCarouselProps> = ({
    categoryId,
    resultCount,
    addedToCartPodId,
    productTileBorderColor,
    showPeek,
    showPeekMobile,
    slidesVisible,
    slidesVisibleMobile,
    slidePadding,
    slidePaddingMobile,
    className,
    ButtonNavigationColor,
    enableHeadline = false,
    headlineText,
    enableNavigationArrows = false,
    showNavigationArrowsMobile = false,
    showNavigationArrowsDesktop = false,
    ctaText,
    ctaHref,
    enableBasicView = false,
    ctaExternal = false,
    ...props
}) => {
    const shouldQuery = typeof categoryId === 'string' && categoryId.length > 0
    const {data, isLoading, error} = useFetchCioProductCategory(
        categoryId,
        resultCount,
        shouldQuery
    )

    const productResults = data?.response?.results

    const link = ctaExternal ? ctaHref : ctaHref

    const productIds = useMemo(() => {
        return productResults ? productResults.map((result: any) => result.data?.id) : []
    }, [productResults])

    const {sfccProducts, isSfccProductsLoading} = useFetchBatchedSfccProducts(productIds)

    if (error) {
        console.error(
            `There was an issue fetching product category for product category ID: ${categoryId}`
        )
    }

    const isDataLoading = isLoading || isSfccProductsLoading || !productResults

    const fetchedProductData = useMemoizedProductData({
        isDataLoading,
        resultCount,
        productResults: productResults || [],
        sfccProducts
    })

    return (
        <div
            className={cn('p-[18px] lg:p-[28px]', {['pr-0 lg:pr-0']: showPeek}, className)}
            {...props}
        >
            <div className="product-carousel-grid-container">
                {enableHeadline && headlineText && (
                    <div className="mb-4">
                        <h2 className="text-emphasis leading-[1.2] text-[26px] lg:text-[36px] font-medium text-center">
                            {headlineText}
                        </h2>
                    </div>
                )}
                <Carousel
                    opts={{loop: true, align: 'start'}}
                    className="h-full flex flex-col justify-center relative"
                    data-testid="productCategoryCarousel"
                >
                    <CarouselContent
                        className="mx-auto"
                        data-testid="productCategoryCarousel-CarouselContent"
                    >
                        {fetchedProductData?.map((result: ProductDataType, i: number) => (
                            <CarouselItem
                                key={`product-${result.id}-${i}`}
                                slidesVisible={slidesVisible || '3.25'}
                                slidesVisibleMobile={slidesVisibleMobile || '2.25'}
                                showPeek={showPeek}
                                showPeekMobile={showPeekMobile}
                                slidePadding={slidePadding}
                                slidePaddingMobile={slidePaddingMobile}
                                data-testid="productCategoryCarousel-CarouselItem"
                            >
                                <ProductTileCms
                                    isProductLoading={result.skeleton}
                                    enableBasicView={enableBasicView}
                                    product={result}
                                    isAddToCart={false}
                                    addedToCartPodId={addedToCartPodId}
                                    className="border-solid border-2 h-full"
                                    style={{borderColor: productTileBorderColor || '#FFFFFF'}}
                                    data-testid="productCategoryCarousel-ProductTileCms"
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <ContentButtonNavigation
                        buttonType={navButtonTypes.FlatButton}
                        buttonColor={ButtonNavigationColor}
                        className="mt-2 lg:mt-4"
                        data-testid="productCategoryCarousel-ContentButtonNavigation"
                    />
                    {enableNavigationArrows && showNavigationArrowsMobile && (
                        <div className="lg:hidden flex translate-y-0 self-center gap-2 mt-2">
                            <CarouselPrevious
                                className="contents"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="productCategoryCarousel-CarouselPrevious"
                            />
                            <CarouselNext
                                className="contents"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="productCategoryCarousel-CarouselNext"
                            />
                        </div>
                    )}
                    {enableNavigationArrows && showNavigationArrowsDesktop && (
                        <div className="hidden lg:flex translate-y-0 self-end gap-2 mt-2 pr-4">
                            <CarouselPrevious
                                className="contents"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="productCategoryCarousel-CarouselPrevious-Desktop"
                            />
                            <CarouselNext
                                className="contents"
                                overlapContent={false}
                                iconAttributes={{className: 'h-6 w-6'}}
                                data-testid="productCategoryCarousel-CarouselNext-Desktop"
                            />
                        </div>
                    )}
                    {ctaText && (
                        <div
                            className={cn('flex justify-center mt-6 mb-4', {
                                'lg:mt-[0px]': showNavigationArrowsDesktop
                            })}
                        >
                            <Button
                                ctaType="button"
                                variant="primary"
                                clickFunction="link"
                                url={link}
                                className="px-12"
                                data-testid="productCategoryCarousel-cta-button"
                            >
                                {ctaText}
                            </Button>
                        </div>
                    )}
                </Carousel>
            </div>
        </div>
    )
}
